import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import RevealImport from "reveal.js"
import "reveal.js/dist/reveal.css"
import "reveal.js/dist/theme/white.css"
import { ArrowLeft, RotateCcw, Lightbulb, PartyPopper } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { toast } from "sonner"
import { fetchNextBatch, reviewCard, type StudyCard, type SrsRating } from "@/api/flashcards"
import { Link } from "react-router-dom"
import { ROUTES } from "@/constants/routes"

interface SessionStats {
  again: number
  hard: number
  good: number
  easy: number
}

// reveal.js v5 运行时支持多实例（new Reveal(el, opts)），
// 但 @types/reveal.js 只声明了单例 namespace，这里定义最小实例接口
interface RevealDeck {
  initialize(): Promise<void>
  destroy(): void
  sync(): void
  slide(indexH: number, indexV?: number, indexF?: number): void
  next(): void
  on(type: string, listener: (event: Event) => void): void
}

const RevealCtor = RevealImport as unknown as new (
  el: HTMLElement,
  options?: Record<string, unknown>,
) => RevealDeck

// ─── 卡片 → reveal.js section HTML ────────────────────────────────

function cardToSectionHTML(card: StudyCard): string {
  const hintHTML = card.hint
    ? `<div class="fc-hint"><span>提示：${escapeHtml(card.hint)}</span></div>`
    : ""
  const tagsHTML = (card.tags ?? []).map((t) => `<span>${escapeHtml(t)}</span>`).join("")
  return `
  <section data-card-id="${card.cardId}">
    <div class="fc-front">${escapeHtml(card.front)}</div>
    ${hintHTML}
    <div class="fragment fc-back"><div class="fc-back-inner">${escapeHtml(card.back)}</div></div>
    ${tagsHTML ? `<div class="fc-tags">${tagsHTML}</div>` : ""}
  </section>`
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}

export default function FlashcardStudyPage() {
  const [searchParams] = useSearchParams()
  const deckId = searchParams.get("deckId") ?? undefined

  const [queue, setQueue] = useState<StudyCard[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sessionKey, setSessionKey] = useState(0)

  const [revealed, setRevealed] = useState(false)
  const [reviewing, setReviewing] = useState(false)
  const [stats, setStats] = useState<SessionStats>({ again: 0, hard: 0, good: 0, easy: 0 })
  const [finished, setFinished] = useState(false)

  const revealRef = useRef<HTMLDivElement>(null)
  const deckRef = useRef<RevealDeck | null>(null)
  const deckReady = useRef(false)
  // 用 ref 保存最新 queue，供 rebuild / reveal 初始化回调使用
  const queueRef = useRef<StudyCard[]>(queue)
  queueRef.current = queue

  const loadBatch = useCallback(() => {
    setLoading(true)
    setError(null)
    fetchNextBatch({ deckId, limit: 20 })
      .then((cards) => {
        setQueue(cards)
        setFinished(cards.length === 0)
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false))
  }, [deckId])

  useEffect(() => {
    loadBatch()
  }, [loadBatch, sessionKey])

  const current = queue[0] ?? null
  const hasCurrent = current !== null
  const totalReviewed = useMemo(
    () => stats.again + stats.hard + stats.good + stats.easy,
    [stats],
  )

  // ─── reveal.js 生命周期：有卡片时挂载运行时 ─────────────────────

  function rebuild(deck: RevealDeck) {
    const slidesEl = revealRef.current?.querySelector(":scope > .slides")
    if (!slidesEl) return
    slidesEl.innerHTML = queueRef.current.map(cardToSectionHTML).join("")
    deck.sync()
    deck.slide(0, 0, 0)
    setRevealed(false)
  }

  useEffect(() => {
    if (!hasCurrent || !revealRef.current) return
    const deck = new RevealCtor(revealRef.current, {
      controls: false,
      keyboard: false,
      touch: false,
      overview: false,
      progress: false,
      hash: false,
      slideNumber: false,
      transition: "slide",
      width: 900,
      height: 560,
    })
    let cancelled = false
    deck.initialize().then(() => {
      if (cancelled) {
        deck.destroy()
        return
      }
      deckRef.current = deck
      deckReady.current = true
      deck.on("fragmentshown", () => setRevealed(true))
      // 数据可能先于运行时就绪，初始化完成后重建一次
      rebuild(deck)
    })
    return () => {
      cancelled = true
      deckRef.current = null
      deckReady.current = false
      try {
        deck.destroy()
      } catch {
        // ignore
      }
    }
    // 仅在"有/无卡片"切换时重新初始化
  }, [hasCurrent])

  // queue 变化（评分、again 重排、重新加载）时重建 slides
  useEffect(() => {
    if (!deckReady.current || !deckRef.current || !hasCurrent) return
    rebuild(deckRef.current)
  }, [queue, hasCurrent])

  // ─── 交互 ───────────────────────────────────────────────────────

  function handleFlip() {
    if (!revealed) {
      deckRef.current?.next()
    }
  }

  async function handleRate(rating: SrsRating) {
    if (!current || reviewing || !revealed) return
    setReviewing(true)
    try {
      await reviewCard({ cardId: current.cardId, rating })
      setStats((prev) => ({ ...prev, [rating]: prev[rating] + 1 }))
      setQueue((prev) => {
        const rest = prev.slice(1)
        // 忘了的卡片重新排到队尾
        return rating === "again" ? [...rest, prev[0]] : rest
      })
      if (queue.length === 1 && rating !== "again") {
        setFinished(true)
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "提交失败")
    } finally {
      setReviewing(false)
    }
  }

  // 键盘：空格翻面；翻面后 1-4 评分
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (!hasCurrent) return
      if (e.code === "Space") {
        e.preventDefault()
        handleFlip()
        return
      }
      if (!revealed || reviewing) return
      const map: Record<string, SrsRating> = {
        Digit1: "again",
        Digit2: "hard",
        Digit3: "good",
        Digit4: "easy",
      }
      const rating = map[e.code]
      if (rating) {
        e.preventDefault()
        handleRate(rating)
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCurrent, revealed, reviewing, queue])

  function handleRestart() {
    setStats({ again: 0, hard: 0, good: 0, easy: 0 })
    setFinished(false)
    setRevealed(false)
    setSessionKey((k) => k + 1)
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="mx-auto max-w-3xl px-6 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <Link to={ROUTES.FLASHCARDS}>
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-1" />
              返回卡组
            </Button>
          </Link>
          {totalReviewed > 0 && (
            <div className="text-sm text-slate-500">已复习 {totalReviewed} 张</div>
          )}
        </div>

        {loading && <div className="text-sm text-slate-500 text-center py-20">加载中...</div>}
        {error && <div className="text-sm text-red-500 text-center py-20">{error}</div>}

        {!loading && !error && finished && (
          <Card>
            <CardContent className="py-12 text-center space-y-4">
              <PartyPopper className="w-10 h-10 mx-auto text-amber-500" />
              <div className="text-lg font-medium">今日复习完成！</div>
              {totalReviewed > 0 ? (
                <div className="flex justify-center gap-3 text-sm">
                  <Badge variant="secondary">忘了 {stats.again}</Badge>
                  <Badge variant="secondary">困难 {stats.hard}</Badge>
                  <Badge variant="secondary">记得 {stats.good}</Badge>
                  <Badge variant="secondary">简单 {stats.easy}</Badge>
                </div>
              ) : (
                <p className="text-sm text-slate-500">当前没有到期的卡片，稍后再来看看吧</p>
              )}
              <div className="flex justify-center gap-2">
                <Button variant="outline" onClick={handleRestart}>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  刷新队列
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {!loading && !error && current && (
          <div className="space-y-4">
            <div className="flex items-center justify-between text-xs text-slate-400">
              <span>{current.deckName}</span>
              <span>剩余 {queue.length} 张</span>
            </div>

            {/* reveal.js 放映区：点击翻面 */}
            <div
              className="fc-stage relative rounded-xl border border-slate-200 overflow-hidden bg-white cursor-pointer"
              onClick={handleFlip}
            >
              <div ref={revealRef} className="reveal">
                <div className="slides" />
              </div>
              {!revealed && (
                <div className="absolute bottom-2 inset-x-0 text-center text-xs text-slate-400 pointer-events-none">
                  点击或按空格揭示答案
                </div>
              )}
            </div>

            {/* 评分按钮：翻面后可用 */}
            <div className="grid grid-cols-4 gap-2">
              <Button
                variant="outline"
                className="border-red-200 text-red-600"
                disabled={!revealed || reviewing}
                onClick={() => handleRate("again")}
              >
                忘了
                <span className="block text-[10px] text-slate-400 ml-1">10分钟</span>
              </Button>
              <Button
                variant="outline"
                className="border-amber-200 text-amber-600"
                disabled={!revealed || reviewing}
                onClick={() => handleRate("hard")}
              >
                困难
              </Button>
              <Button
                variant="outline"
                className="border-blue-200 text-blue-600"
                disabled={!revealed || reviewing}
                onClick={() => handleRate("good")}
              >
                记得
              </Button>
              <Button
                variant="outline"
                className="border-emerald-200 text-emerald-600"
                disabled={!revealed || reviewing}
                onClick={() => handleRate("easy")}
              >
                简单
              </Button>
            </div>

            <div className="text-center text-xs text-slate-400">
              空格翻面 · 翻面后按 1 忘了 / 2 困难 / 3 记得 / 4 简单
            </div>
          </div>
        )}
      </div>

      <style>{`
        .fc-stage { height: 440px; }
        .fc-stage .reveal { width: 100%; height: 100%; }
        .fc-stage .reveal .slides { text-align: center; }
        .fc-stage .reveal .slides section { padding: 24px; }
        .fc-stage .fc-front {
          font-size: 1.5rem; font-weight: 500; line-height: 1.7;
          max-width: 40em; margin: 0.6em auto; white-space: pre-wrap; word-break: break-word;
        }
        .fc-stage .fc-hint {
          font-size: 0.9rem; color: #94a3b8;
          display: flex; align-items: center; justify-content: center; gap: 6px;
        }
        .fc-stage .fc-back { margin-top: 1em; }
        .fc-stage .fc-back-inner {
          display: inline-block; text-align: left; max-width: 40em;
          background: rgba(16, 185, 129, 0.08); border: 1px solid rgba(16, 185, 129, 0.35);
          border-radius: 12px; padding: 14px 20px;
          font-size: 1.1rem; line-height: 1.7; white-space: pre-wrap; word-break: break-word;
          color: #0f172a;
        }
        .fc-stage .fc-tags { margin-top: 1em; font-size: 0.75rem; color: #94a3b8; }
        .fc-stage .fc-tags span {
          display: inline-block; border: 1px solid #cbd5e1; border-radius: 999px;
          padding: 2px 10px; margin: 0 3px;
        }
        /* 覆盖 reveal 主题的默认正文样式，保持可读字号 */
        .fc-stage .reveal { font-size: 16px; }
      `}</style>
    </div>
  )
}
